import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {WidgetDetail} from '../../../../../../domain/widget/model/widget-detail';
import {Chart} from 'angular-highcharts';
import {AmountFormatterService} from '../../../../../services/amount-formatter.service';
import {TranslateService} from '@ngx-translate/core';
import {DomainUrlService} from '../../../../../../application/config/domain-url.service';
import {WidgetSize} from '../../../../../../domain/widget/model/widget-size.enum';
import {DebtMonthlyPayment} from '../../../../../../domain/debt-monthly-payments/model/debt-monthly-payment';
import {DebtMonthlyPayments} from '../../../../../../domain/debt-monthly-payments/model/debt-monthly-payments';
import {UserContextService} from '../../../../../../application/app-context/user-context/user-context.service';
import {ApplicationName} from '../../../../../../domain/core/model/aplication-name.enum';

@Component({
             selector: 'app-widget-debt-monthly-payments',
             templateUrl: './home-dashboard-widget-debt-monthly-payments.component.html'
           })
export class HomeDashboardWidgetDebtMonthlyPaymentsComponent implements OnInit {

  public AMORTIZATION_COLOR = '#280484';
  public INTERESTS_COLOR = '#ec484b';

  @Input()
  public widget: WidgetDetail | undefined;
  @Input()
  public debtMonthlyPayments: DebtMonthlyPayments;

  public nonNullDebtMonthlyPayments: Array<DebtMonthlyPayment>;
  public debtMonthlyPayment: DebtMonthlyPayment;
  public WidgetSize = WidgetSize;
  public chart: Chart;
  public insitoAccountId;

  @Output() actionEvent = new EventEmitter<string>();

  constructor(public amountFormatterService: AmountFormatterService,
              private translateService: TranslateService,
              private domainUrl: DomainUrlService,
              private userContextService: UserContextService) {
    const userContext = this.userContextService.getUserContext();
    const currentAccount = userContext.user.accounts.find(account => account.accountId === userContext.accountId);
    this.insitoAccountId = currentAccount.applications.find(application => (application.targetApp === ApplicationName.INSITO) && application.accountSynchId !== null).accountSynchId;
  }

  public ngOnInit(): void {
    this.nonNullDebtMonthlyPayments = this.debtMonthlyPayments.payments.filter(debtMonthlyPayment => debtMonthlyPayment.flow !== null);
    this.debtMonthlyPayment = this.getInitialMonthlyPayment();

    if (this.debtMonthlyPayment != null) {
      this.chart = this.loadChart();
    }
  }

  public onClickOnPreviousPayment(): void {
    const currentIndex = this.nonNullDebtMonthlyPayments.indexOf(this.debtMonthlyPayment);
    this.debtMonthlyPayment = this.nonNullDebtMonthlyPayments[currentIndex - 1];
    this.chart = this.loadChart();
    this.actionEvent.emit('widget.amplitude.carousel-use');
  }

  public onClickOnNextPayment(): void {
    const currentIndex = this.nonNullDebtMonthlyPayments.indexOf(this.debtMonthlyPayment);
    this.debtMonthlyPayment = this.nonNullDebtMonthlyPayments[currentIndex + 1];
    this.chart = this.loadChart();
    this.actionEvent.emit('widget.amplitude.carousel-use');
  }

  public isFirstPayment(): boolean {
    return this.nonNullDebtMonthlyPayments.indexOf(this.debtMonthlyPayment) === 0;
  }

  public isLastPayment(): boolean {
    return this.nonNullDebtMonthlyPayments.indexOf(this.debtMonthlyPayment) === this.nonNullDebtMonthlyPayments.length - 1;
  }

  public getProcessPaymentOrdersLabel(): string {
    const paymentOrderToProcessCount = this.debtMonthlyPayment.paymentOrderCount - this.debtMonthlyPayment.processedPaymentOrderCount;
    return this.translateService.instant('widget.debt-monthly-payments.process-payment-orders', {
      isAllPaymentOrdersProcessed: paymentOrderToProcessCount === this.debtMonthlyPayment.paymentOrderCount,
      paymentOrderToProcessCount,
      totalPaymentOrderCount: this.debtMonthlyPayment.paymentOrderCount
    });
  }

  public getProcessPaymentOrdersUrl(): string {
    if (this.debtMonthlyPayments.paymentOrderManagementActivated) {
      return `${this.domainUrl.getInsitoUrl()}?accountId=${this.insitoAccountId}&redirectLink=extractionMandatement&startDate=${this.debtMonthlyPayment.startDate}&endDate=${this.debtMonthlyPayment.endDate}`;
    }
    return `${this.domainUrl.getInsitoUrl()}?accountId=${this.insitoAccountId}&redirectLink=vosEcheances&refDate=${this.debtMonthlyPayment.startDate}&month=${new Date(
      this.debtMonthlyPayment.startDate).getMonth() + 1}&mode=CONSOLIDATED&detail=true&filter=true`;
  }

  private getInitialMonthlyPayment(): DebtMonthlyPayment {
    const now = new Date();

    let debtMonthlyPayment =
      this.nonNullDebtMonthlyPayments.find(payment => new Date(payment.startDate).getMonth()
                                                      === now.getMonth()
                                                      && new Date(payment.startDate).getFullYear()
                                                      === now.getFullYear());

    if (debtMonthlyPayment == null) {
      debtMonthlyPayment = this.nonNullDebtMonthlyPayments.reduce((p1, p2) => new Date(p1.startDate) < new Date(p2.startDate) ? p1 : p2);
    }

    return debtMonthlyPayment;
  }

  private loadChart(): Chart {
    if (this.hasNoMonthlyPayment()) {
      return null;
    }
    const spec = {
      credits: {
        enabled: false
      },
      chart: {
        type: 'pie',
        spacing: [1, 1, 1, 1],
        marginTop: 0,
        spacingTop: 0,
        spacingBottom: 0,
        marginBottom: 0,
        plotBorderColor: '#6a7480',
        backgroundColor: '#ffffff',
        plotBorderWidth: 0,
        style: {
          fontFamily: 'Arial, Helvetica, sans-serif',
          'font-family': 'Arial, Helvetica, sans-serif'
        }
      },
      title: {
        text: this.getGraphTitle(),
        align: 'center',
        verticalAlign: 'middle',
        y: 28
      },
      tooltip: {
        pointFormat: '{series.name} <b>{point.formattedAmount}</b>'
      },
      subtitle: {
        text: null
      },
      exporting: {
        enabled: false
      },
      accessibility: {
        announceNewData: {
          enabled: true
        },
        point: {
          valueSuffix: '%'
        }
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          useHTML: true,
          dataLabels: {
            enabled: this.widget.size === this.WidgetSize.SMALL,
            crop: true,
            distance: this.getDistance(),
            textOverflow: 'ellipsis',
            style: {
              fontWeight: 'bold',
              color: this.getColor()
            }
          },
          borderWidth: 10,
          borderColor: null,
          startAngle: -120,
          endAngle: 120,
          center: ['50%', '75%'],
          size: '160%'
        }
      },
      series: [{
        name: '',
        type: 'pie',
        innerSize: '100%',
        data: [
          {
            name: this.translateService.instant('widget.debt-monthly-payments.amortization'),
            y: this.debtMonthlyPayment.amortization ? this.debtMonthlyPayment.amortization.value : null,
            color: this.AMORTIZATION_COLOR,
            formattedAmount: this.amountFormatterService.format(this.debtMonthlyPayment.amortization, '0.0a'),
            zIndex: 10000
          },
          {
            name: this.translateService.instant('widget.debt-monthly-payments.interests'),
            y: this.debtMonthlyPayment.interests ? this.debtMonthlyPayment.interests.value : null,
            color: this.INTERESTS_COLOR,
            formattedAmount: this.amountFormatterService.format(this.debtMonthlyPayment.interests, '0.0a'),
            zIndex: 2
          }
        ]
      }],
      legend: {
        enabled: false
      }
    };

    return new Chart(spec as any);
  }

  private getColor(): string {
    return this.widget.size === this.WidgetSize.LARGE ? 'white'
                                                      : 'black';
  }

  private getDistance(): number {
    return this.widget.size === this.WidgetSize.LARGE ? -50
                                                      : 0;
  }

  private getGraphTitle(): string {
    if (this.hasNoMonthlyPayment()) {
      return `<h4>${this.translateService.instant('widget.debt-monthly-payments.no-payment')}</h4>`;
    }
    return `<h4 class="c-medium-gray font-bold f-18">${this.amountFormatterService.format(this.debtMonthlyPayment.flow, '0.0a')}</h4>`
           + `<br/>`
           + `<span class="c-medium-gray f-14">${this.translateService.instant('widget.debt-monthly-payments.flow')}</span>`;
  }

  private hasNoMonthlyPayment(): boolean {
    return this.debtMonthlyPayment === null || this.debtMonthlyPayment.flow === null;
  }
}
